import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../../config/environment';
import MarkdownToolbar from './MarkdownToolbar';
import { useRef } from 'react';

export default function EditEventForm({ event, onSuccess, onCancel }) {
  const descriptionRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    type: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    timezone: '',
    location_city: '',
    location_name: '',
    location_address: '',
    parking_instructions: '',
    capacity: 30,
    blocked_seats: 0,
    requires_rsvp: true,
    allow_guests: true,
    max_guests_per_rsvp: 1,
    visibility: '',
    enable_donation_link: false,
    donation_link_url: '',
    donation_link_text: 'Support This Cause'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill form with event data
  useEffect(() => {
    if (event) {
      // Convert UTC datetime to event timezone, then split into date and time
      const formatDateTime = (utcDateString, eventTimezone) => {
        const utcDate = new Date(utcDateString);
        
        // Convert to event timezone using Intl
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: eventTimezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        
        const parts = {};
        formatter.formatToParts(utcDate).forEach(part => {
          parts[part.type] = part.value;
        });
        
        // Return separate date and time
        return {
          date: `${parts.year}-${parts.month}-${parts.day}`,
          time: `${parts.hour}:${parts.minute}`
        };
      };
      
      const startDateTime = formatDateTime(event.start_datetime, event.timezone || 'America/New_York');
      const endDateTime = formatDateTime(event.end_datetime, event.timezone || 'America/New_York');
      
      setFormData({
        title: event.title || '',
        slug: event.slug || '',
        description: event.description || '',
        type: event.type || 'networking_breakfast',
        start_date: startDateTime.date,
        start_time: startDateTime.time,
        end_date: endDateTime.date,
        end_time: endDateTime.time,
        timezone: event.timezone || 'America/New_York',
        location_city: event.location_city || '',
        location_name: event.location_name || '',
        location_address: event.location_address || '',
        parking_instructions: event.parking_instructions || '',
        capacity: event.capacity || 30,
        blocked_seats: event.blocked_seats || 0,
        requires_rsvp: event.requires_rsvp !== false,
        allow_guests: event.allow_guests !== false,
        max_guests_per_rsvp: event.max_guests_per_rsvp || 1,
        visibility: event.visibility || 'draft',
        enable_donation_link: event.enable_donation_link || false,
        donation_link_url: event.donation_link_url || '',
        donation_link_text: event.donation_link_text || 'Support This Cause'
      });
    }
  }, [event]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('admin_token');
    
    try {
      // Combine date and time fields into datetime format
      const start_datetime = `${formData.start_date}T${formData.start_time}`;
      const end_datetime = `${formData.end_date}T${formData.end_time}`;
      
      const eventData = {
        ...formData,
        start_datetime,
        end_datetime,
        timezone: formData.timezone,
        capacity: formData.capacity === '' ? null : parseInt(formData.capacity),
        blocked_seats: parseInt(formData.blocked_seats || 0),
        enable_donation_link: formData.enable_donation_link,
        donation_link_url: formData.enable_donation_link ? formData.donation_link_url : null,
        donation_link_text: formData.enable_donation_link ? formData.donation_link_text : 'Support This Cause'
      };
      
      // Remove the separate date/time fields before sending
      delete eventData.start_date;
      delete eventData.start_time;
      delete eventData.end_date;
      delete eventData.end_time;

      const response = await fetch(`${API_BASE_URL}/api/admin/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });

      const result = await response.json(); // Read response once
      
      if (!response.ok) {
        throw new Error(result.detail || 'Failed to update event');
      }
      
      // Show notification message
      if (result.significant_changes) {
        alert(`Event updated! ${result.rsvps_notified} attendees notified of changes.`);
      } else {
        alert('Event updated successfully!');
      }
      
      onSuccess(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!event) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onCancel} data-lenis-prevent>
      <div 
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Fixed */}
        <div className="flex justify-between items-center p-8 pb-4 border-b border-[#E1DFD7]">
          <h2 className="font-heading text-2xl font-semibold text-[#0B2240]">Edit Event</h2>
          <button onClick={onCancel} className="text-[#4C5A70] hover:text-[#0B2240]">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form - Scrollable */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-8 py-6 space-y-6"
            style={{ maxHeight: 'calc(85vh - 140px)' }}
          >
          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Event Title *</label>
            <input type="text" name="title" value={formData.title} onChange={handleChange} required
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">URL Slug *</label>
            <input type="text" name="slug" value={formData.slug} onChange={handleChange} required
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Description *</label>
            <MarkdownToolbar 
              textareaRef={descriptionRef}
              onInsert={(newValue) => setFormData(prev => ({ ...prev, description: newValue }))}
            />
            <textarea 
              ref={descriptionRef}
              name="description" 
              value={formData.description} 
              onChange={handleChange} 
              required 
              rows={6}
              className="w-full px-4 py-3 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3] font-mono text-sm" 
            />
            <p className="text-xs text-[#4C5A70] mt-1">Use toolbar buttons or type markdown directly</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Event Type *</label>
              <select name="type" value={formData.type} onChange={handleChange}
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
                <option value="networking_breakfast">Networking Breakfast</option>
                <option value="free_coffee_friday">Free Coffee Friday</option>
                <option value="charity">Charity Event</option>
                <option value="client_event">Client Event</option>
                <option value="workshop">Workshop</option>
                <option value="special">Special Event</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Visibility *</label>
              <select name="visibility" value={formData.visibility} onChange={handleChange}
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
                <option value="draft">Draft</option>
                <option value="hidden">Hidden</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Start Date *</label>
              <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} required
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Start Time *</label>
              <input type="time" name="start_time" value={formData.start_time} onChange={handleChange} required
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
              <p className="text-xs text-[#1FA3A3] mt-1">⏰ Enter time in {formData.timezone?.split('/')[1]?.replace('_', ' ')} timezone</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">End Date *</label>
              <input type="date" name="end_date" value={formData.end_date} onChange={handleChange} required
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">End Time *</label>
              <input type="time" name="end_time" value={formData.end_time} onChange={handleChange} required
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
              <p className="text-xs text-[#1FA3A3] mt-1">⏰ Enter time in {formData.timezone?.split('/')[1]?.replace('_', ' ')} timezone</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Timezone *</label>
            <select name="timezone" value={formData.timezone} onChange={handleChange}
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
              <option value="America/New_York">Eastern (ET) - EST/EDT</option>
              <option value="America/Chicago">Central (CT) - CST/CDT</option>
              <option value="America/Denver">Mountain (MT) - MST/MDT</option>
              <option value="America/Los_Angeles">Pacific (PT) - PST/PDT</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Location City *</label>
            <select name="location_city" value={formData.location_city} onChange={handleChange}
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
              <option value="Charlotte">Charlotte</option>
              <option value="Mooresville">Mooresville</option>
              <option value="Winston-Salem">Winston-Salem</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Venue Name *</label>
            <input type="text" name="location_name" value={formData.location_name} onChange={handleChange} required
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Full Address *</label>
            <input type="text" name="location_address" value={formData.location_address} onChange={handleChange} required
              className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0B2240] mb-2">Parking Instructions</label>
            <textarea name="parking_instructions" value={formData.parking_instructions} onChange={handleChange} rows={2}
              placeholder="e.g., Free parking available in the rear lot"
              className="w-full px-4 py-3 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Capacity</label>
              <input type="number" name="capacity" value={formData.capacity} onChange={handleChange} min="1"
                placeholder="Leave empty for unlimited"
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Blocked Seats</label>
              <input type="number" name="blocked_seats" value={formData.blocked_seats} onChange={handleChange} min="0"
                placeholder="0"
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
              <p className="text-xs text-[#4C5A70] mt-1">🔒 Reserved for VIPs/staff (hidden from public)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0B2240] mb-2">Max Guests Per RSVP</label>
              <select name="max_guests_per_rsvp" value={formData.max_guests_per_rsvp || 1} onChange={handleChange}
                className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]">
                <option value="0">No Guests Allowed</option>
                <option value="1">Allow +1 Guest</option>
                <option value="2">Allow +2 Guests</option>
                <option value="3">Allow +3 Guests</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input type="checkbox" name="requires_rsvp" checked={formData.requires_rsvp} onChange={handleChange}
                  className="w-4 h-4 rounded" />
                <span className="text-sm font-medium text-[#0B2240]">Requires RSVP</span>
              </label>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input type="checkbox" name="allow_guests" checked={formData.allow_guests !== false} onChange={handleChange}
                  className="w-4 h-4 rounded" />
                <span className="text-sm font-medium text-[#0B2240]">Allow Guests</span>
              </label>
            </div>
          </div>

          {/* Donation Link Section */}
          <div className="border-t border-[#E1DFD7] pt-6">
            <div className="flex items-center gap-2 mb-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" name="enable_donation_link" checked={formData.enable_donation_link} onChange={handleChange}
                  className="w-4 h-4 rounded" />
                <span className="text-sm font-medium text-[#0B2240]">Enable Donation Link</span>
              </label>
            </div>
            
            {formData.enable_donation_link && (
              <div className="space-y-4 bg-[#F7F6F2] p-4 rounded-lg">
                <div>
                  <label className="block text-sm font-medium text-[#0B2240] mb-2">Donation Link URL *</label>
                  <input type="url" name="donation_link_url" value={formData.donation_link_url} onChange={handleChange}
                    placeholder="https://give.kgob.com/charity-event"
                    required={formData.enable_donation_link}
                    className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
                  <p className="text-xs text-[#4C5A70] mt-1">Full URL to external donation page</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0B2240] mb-2">Button Text</label>
                  <input type="text" name="donation_link_text" value={formData.donation_link_text} onChange={handleChange}
                    placeholder="Support This Cause"
                    className="w-full h-11 px-4 rounded-lg border border-[#E1DFD7] focus:ring-2 focus:ring-[#1FA3A3]" />
                  <p className="text-xs text-[#4C5A70] mt-1">Text displayed on the donation button</p>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-[#C7362E]/10 border border-[#C7362E] rounded-lg p-4">
              <p className="text-[#C7362E] text-sm font-medium">⚠️ {error}</p>
            </div>
          )}

          <div className="bg-[#E3C96E]/10 border border-[#D4AF37] rounded-lg p-4">
            <p className="text-[#0B2240] text-sm font-medium">⚠️ Important:</p>
            <p className="text-[#4C5A70] text-sm mt-1">
              If you change the date, time, or location, all confirmed attendees will be notified and asked to confirm their attendance.
            </p>
          </div>
          </div>

          {/* Footer - Fixed */}
          <div className="px-8 py-4 border-t border-[#E1DFD7] bg-[#F7F6F2]">
            <div className="flex gap-3">
              <button type="submit" disabled={loading}
                className="flex-1 h-12 bg-[#0B2240] text-white rounded-lg font-semibold hover:bg-[#0A1D35] disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Updating...
                  </>
                ) : 'Update Event'}
              </button>
              <button type="button" onClick={onCancel}
                className="px-6 h-12 border border-[#E1DFD7] text-[#0B2240] rounded-lg hover:bg-[#EDEBE6]">
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
